import { Link } from "react-router-dom";

const examples = [
  {
    investment: "£20,000",
    annualIncome: "£2,000",
    monthlyIncome: "~£166.67",
    totalIncome: "£10,000",
    capitalReturn: "£20,000",
    totalReturn: "£30,000",
  },
  {
    investment: "£100,000",
    annualIncome: "£10,000",
    monthlyIncome: "~£833.33",
    totalIncome: "£50,000",
    capitalReturn: "£100,000",
    totalReturn: "£150,000",
  },
];

export default function ReturnsSection() {
  return (
    <section className="bg-white py-20 md:py-24">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">
            10.00% Per Annum Target
          </span>
          <h2
            className="text-3xl md:text-4xl font-bold text-[#071A2E]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Illustrative Returns
          </h2>
          <p className="text-slate-500 text-sm mt-3 max-w-lg mx-auto">
            The following examples illustrate potential returns over the 5-year term. These are illustrative only and are not guaranteed. Capital may be at risk.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {examples.map((ex) => (
            <div
              key={ex.investment}
              className="bg-[#EEF2F9] rounded-2xl overflow-hidden"
            >
              {/* Header */}
              <div className="bg-[#071A2E] px-7 py-6">
                <p className="text-slate-400 text-xs font-semibold uppercase tracking-widest mb-1">
                  Example Investment
                </p>
                <p
                  className="text-4xl font-bold text-[#C9A84C]"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  {ex.investment}
                </p>
              </div>
              {/* Breakdown */}
              <div className="p-7">
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-400 mb-1">Annual Income</p>
                    <p className="font-bold text-[#071A2E] text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {ex.annualIncome}
                    </p>
                  </div>
                  <div className="bg-white rounded-xl p-4 text-center">
                    <p className="text-xs text-slate-400 mb-1">Monthly Income</p>
                    <p className="font-bold text-[#C9A84C] text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
                      {ex.monthlyIncome}
                    </p>
                  </div>
                </div>

                <p className="text-xs text-slate-400 font-semibold uppercase tracking-widest mb-3">
                  Over 5 Years
                </p>
                <div className="flex flex-col gap-2 mb-5">
                  {[
                    { label: "Total Income Received", value: ex.totalIncome },
                    { label: "Capital Returned at Maturity", value: ex.capitalReturn },
                  ].map((row) => (
                    <div key={row.label} className="flex justify-between items-center bg-white rounded-lg px-4 py-3">
                      <span className="text-slate-500 text-xs">{row.label}</span>
                      <span className="font-semibold text-[#071A2E] text-sm">{row.value}</span>
                    </div>
                  ))}
                </div>

                <div className="flex justify-between items-center bg-[#071A2E] rounded-xl px-5 py-4">
                  <span className="text-slate-300 text-sm font-semibold">Total Return</span>
                  <span
                    className="text-[#C9A84C] text-xl font-bold"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {ex.totalReturn}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Security structure */}
        <div className="bg-[#EEF2F9] rounded-2xl p-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="text-[#C9A84C] text-xs font-semibold uppercase tracking-widest block mb-3">
                Investor Protection
              </span>
              <h3
                className="text-xl font-bold text-[#071A2E] mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Security Structure
              </h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                First legal charges are held over all underlying properties by an Independent Security Trustee appointed solely to protect investor interests.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {[
                { icon: "ri-lock-2-line", text: "First legal charges over all underlying properties" },
                { icon: "ri-user-star-line", text: "Independent Security Trustee appointed on behalf of investors" },
                { icon: "ri-eye-line", text: "Ongoing compliance monitoring throughout the term" },
                { icon: "ri-shield-flash-line", text: "Security enforcement capability where required" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3 bg-white rounded-xl px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A84C]/15 text-[#C9A84C] flex-shrink-0">
                    <i className={`${item.icon} text-sm leading-none`} />
                  </div>
                  <span className="text-[#071A2E] text-xs font-medium">{item.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-slate-400 text-xs max-w-2xl mx-auto mb-5">
            These illustrations are based on the target return of 10.00% per annum and are for illustrative purposes only. Returns are not guaranteed and capital may be at risk. Tax treatment depends on individual circumstances — investors should seek independent tax advice.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-[#C9A84C] text-[#071A2E] font-semibold text-sm hover:bg-[#B8953B] transition-colors cursor-pointer whitespace-nowrap"
          >
            Express Interest
            <i className="ri-arrow-right-line leading-none" />
          </Link>
        </div>
      </div>
    </section>
  );
}
